import { useState, useCallback, useMemo } from 'react';
import {
  Modal,
  ModalHeader,
  ModalBody,
  ModalFooter,
  Button,
  FormGroup,
  Label,
  Input,
  Spinner,
  FormFeedback,
} from 'reactstrap';
import {
  useCreateValueListMutation,
  useUpdateValueListMutation,
  useCheckBatchValueListOptionUsageQuery,
} from '@api/value-list';
import { ValueListPlain, Option } from '@g17eco/types/valueList';
import { generateToast, toastFromError } from '@g17eco/molecules/toasts';
import { handleRouteError } from '../../../logger';
import { OptionInput, validateValueListForm, PENDING_OPTION_ERROR_KEY } from './value-list-utils';
import { SimpleTooltip } from '@g17eco/molecules/simple-tooltip';

/**
 * Shared modal for creating and editing value lists.
 * Mode is inferred from the presence of valueList prop:
 * - If valueList is provided → Edit mode
 * - If valueList is undefined → Create mode
 */

interface Props {
  isOpen: boolean;
  onClose: () => void;
  initiativeId: string;
  valueList?: ValueListPlain;
  onSuccess: (valueList: ValueListPlain) => void;
}

const generateCodeFromName = (name: string): string =>
  name
    .trim()
    .toLowerCase()
    .replace(/[^a-z0-9]/g, '-')
    .replace(/-+/g, '-')
    .replace(/^-|-$/g, '');

export const ValueListModal = ({ isOpen, onClose, initiativeId, valueList, onSuccess }: Props) => {
  // Infer mode from valueList presence
  const isEditMode = !!valueList;

  // Initialize state based on mode
  const [name, setName] = useState(valueList?.name ?? '');
  const [description, setDescription] = useState(valueList?.description ?? '');
  const [options, setOptions] = useState<OptionInput[]>(() =>
    valueList
      ? valueList.options.map((opt) => ({
          code: opt.code,
          name: opt.name,
          isOriginal: true,
        }))
      : [],
  );
  // Single pending option: null = hidden, string = input value being typed
  const [pendingOption, setPendingOption] = useState<string | null>(!isEditMode ? '' : null);
  const [errors, setErrors] = useState<Record<string, string>>({});

  // API mutations
  const [createValueList, { isLoading: isCreating }] = useCreateValueListMutation();
  const [updateValueList, { isLoading: isUpdating }] = useUpdateValueListMutation();
  const isLoading = isCreating || isUpdating;

  // Edit-only: Get original option codes for batch usage check
  const originalOptionCodes = useMemo(() => (valueList ? valueList.options.map((opt) => opt.code) : []), [valueList]);

  // Edit-only: Batch query to check usage for all original options at once
  const {
    data: batchUsage,
    error: batchUsageError,
    isLoading: isBatchUsageLoading,
  } = useCheckBatchValueListOptionUsageQuery(
    { listId: valueList?._id ?? '', optionCodes: originalOptionCodes },
    { skip: !isEditMode || originalOptionCodes.length === 0 },
  );

  const handleCreateNewOption = () => {
    if (pendingOption === null) {
      setPendingOption('');
    }
  };

  const handleAddPendingOption = () => {
    if (!pendingOption?.trim()) {
      setErrors((prev) => ({ ...prev, [PENDING_OPTION_ERROR_KEY]: 'Name is required' }));
      return;
    }

    const code = generateCodeFromName(pendingOption);

    // Check if code already exists in options
    if (options.some((opt) => opt.code === code)) {
      setErrors((prev) => ({ ...prev, [PENDING_OPTION_ERROR_KEY]: 'An option with this name/code already exists' }));
      return;
    }

    setOptions((prev) => [...prev, { code, name: pendingOption.trim(), isOriginal: false }]);
    setPendingOption(null);
    setErrors((prev) => {
      const newErrors = { ...prev };
      delete newErrors[PENDING_OPTION_ERROR_KEY];
      return newErrors;
    });
  };

  const handleRemoveOption = useCallback((code: string) => {
    setOptions((prev) => prev.filter((opt) => opt.code !== code));
  }, []);

  const handlePendingOptionChange = (value: string) => {
    setPendingOption(value);
    if (errors[PENDING_OPTION_ERROR_KEY]) {
      setErrors((prev) => {
        const newErrors = { ...prev };
        delete newErrors[PENDING_OPTION_ERROR_KEY];
        return newErrors;
      });
    }
  };

  const validate = (): boolean => {
    const newErrors = validateValueListForm(name, options);
    setErrors(newErrors);
    return Object.keys(newErrors).length === 0;
  };

  const handleSubmit = async () => {
    if (!validate()) {
      return;
    }

    const validOptions: Option[] = options.map((opt) => ({
      code: opt.code.trim(),
      name: opt.name.trim(),
    }));

    try {
      const result = await (
        isEditMode
          ? updateValueList({
              id: valueList._id,
              name: name.trim(),
              description: description.trim(),
              options: validOptions,
              ownerId: initiativeId,
            })
          : createValueList({
              name: name.trim(),
              description: description.trim(),
              options: validOptions,
              ownerId: initiativeId,
            })
      ).unwrap();

      generateToast({
        color: 'success',
        title: 'Success',
        message: isEditMode ? 'Value list updated successfully' : 'Value list created successfully',
      });

      onSuccess(result);
    } catch (error) {
      const mode = isEditMode ? 'edit' : 'create';
      handleRouteError(error as Error, { context: `ValueListModal.handleSubmit (${mode})` });
      toastFromError({
        error,
        title: isEditMode ? 'Error updating value list' : 'Error creating value list',
      });
    }
  };

  const handleClose = () => {
    onClose();
  };

  const isOptionInUse = (opt: OptionInput): boolean => isBatchUsageLoading || (batchUsage?.[opt.code]?.utrvCount ?? 0) > 0;

  const modalTitle = isEditMode ? 'Manage Value List' : 'Create New Value List';
  const submitButtonText = isEditMode ? 'Update' : 'Create';

  // Proactive validation - button disabled until form has minimum valid data
  const isFormValid = name.trim().length > 0 && options.length > 0;

  return (
    <Modal isOpen={isOpen} backdrop='static' toggle={handleClose} size='lg' scrollable>
      <ModalHeader toggle={handleClose} className='border-bottom-0'>
        <span className='fw-bold text-ThemePrimary'>{modalTitle}</span>
      </ModalHeader>
      <ModalBody className='py-0'>
        <FormGroup>
          <Label className='small text-muted mb-1'>List name</Label>
          <Input
            value={name}
            onChange={(e) => {
              setName(e.target.value);
              if (errors.name) {
                setErrors((prev) => ({ ...prev, name: '' }));
              }
            }}
            placeholder='Enter value list name'
            invalid={!!errors.name}
          />
          <FormFeedback>{errors.name}</FormFeedback>
        </FormGroup>

        <FormGroup>
          <Label className='small text-muted mb-1'>List description</Label>
          <Input
            type='textarea'
            value={description}
            onChange={(e) => setDescription(e.target.value)}
            placeholder='Enter value list description'
            rows={2}
          />
        </FormGroup>

        <div className='my-4'>
          <Button
            block
            className='rounded background-ThemeNeutralsExtralight border-ThemeNeutralsMedium text-ThemePrimary'
            onClick={handleCreateNewOption}
            disabled={pendingOption !== null}
          >
            Create New Option
          </Button>
        </div>

        {pendingOption !== null && (
          <FormGroup className='mb-3'>
            <Label className='small text-muted mb-1'>Option</Label>
            <div className='d-flex align-items-center gap-3'>
              <div className='flex-grow-1'>
                <Input
                  value={pendingOption}
                  onChange={(e) => handlePendingOptionChange(e.target.value)}
                  placeholder='Enter Value'
                  invalid={!!errors[PENDING_OPTION_ERROR_KEY]}
                />
              </div>
              <Button color='link' className='text-decoration-none p-0' onClick={handleAddPendingOption}>
                Add
              </Button>
            </div>
            {errors[PENDING_OPTION_ERROR_KEY] && (
              <div className='text-danger small mt-1'>{errors[PENDING_OPTION_ERROR_KEY]}</div>
            )}
          </FormGroup>
        )}

        <div className='mt-4'>
          <div className='px-3 py-2 background-ThemeBgToolbar fw-bold small text-uppercase'>OPTION</div>
          <div className='overflow-auto' style={{ maxHeight: '300px' }}>
            {options.length === 0 ? (
              <div className='p-3 text-center text-muted small'>No options defined</div>
            ) : (
              options.map((opt, index) => (
                <div
                  key={opt.code}
                  className={`d-flex align-items-center justify-content-between px-3 py-2 ${
                    index < options.length - 1 ? 'border-bottom' : ''
                  }`}
                >
                  <span>{opt.name}</span>
                  <div className='d-flex align-items-center gap-3'>
                    {isEditMode && opt.isOriginal && isBatchUsageLoading ? (
                      <Spinner size='sm' className='text-muted' />
                    ) : isOptionInUse(opt) ? (
                      <SimpleTooltip text={`Cannot delete ${opt.name} (in use)`}>
                        <Button color='link' className='p-0' disabled aria-label={`Cannot delete ${opt.name} (in use)`}>
                          <i className='fal fa-trash-slash' />
                        </Button>
                      </SimpleTooltip>
                    ) : (
                      <Button
                        color='link'
                        className='p-0 text-muted'
                        onClick={() => handleRemoveOption(opt.code)}
                        aria-label={`Delete ${opt.name}`}
                      >
                        <i className='fal fa-trash-alt' />
                      </Button>
                    )}
                  </div>
                </div>
              ))
            )}
          </div>
        </div>

        {isEditMode && batchUsageError && (
          <div className='alert alert-warning mt-3 mb-0 small py-2'>
            <i className='fal fa-exclamation-triangle me-2' />
            Unable to check option usage status.
          </div>
        )}
      </ModalBody>
      <ModalFooter className='border-top-0 pt-4'>
        <Button color='link' className='text-muted text-decoration-none' onClick={handleClose} disabled={isLoading}>
          Cancel
        </Button>
        <Button
          color='primary'
          className='px-4'
          onClick={handleSubmit}
          disabled={isLoading || !isFormValid || Object.keys(errors).length > 0}
        >
          {isLoading ? <Spinner size='sm' /> : submitButtonText}
        </Button>
      </ModalFooter>
    </Modal>
  );
};
